import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { ConfigProvider, theme as antdTheme } from "antd";

import { RootState } from "../store/store";
import { useDashboardTheme } from "../hooks/useDashboardTheme";
import {
  antdTokensFor,
  dashboardThemeVars,
  isDarkPalette,
  paletteFor,
} from "../config/dashboardTheme";

type AppThemeValue = ReturnType<typeof useDashboardTheme>;

const AppThemeContext = createContext<AppThemeValue | null>(null);

/**
 * The store's theme, for the whole app.
 *
 * Holds the single source of truth for the chosen preset: it publishes the
 * `--dash-*` custom properties on <html> so every page (not just the dashboard)
 * inherits them, and feeds antd's ConfigProvider from the same palette so
 * Tables, Modals, Drawers and Selects follow the theme rather than sitting on
 * default white.
 *
 * Because the state lives here rather than in the dashboard, previewing a theme
 * in the appearance drawer restyles the entire app live.
 */
export const AppThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const userId = useSelector(
    (state: RootState) => state.user?.profileData?.data?.id,
  );
  const value = useDashboardTheme(userId);
  const { preset, mode } = value;

  useEffect(() => {
    const root = document.documentElement;
    const vars = dashboardThemeVars(preset, mode) as Record<string, string>;
    Object.keys(vars).forEach((key) => {
      if (key.startsWith("--")) root.style.setProperty(key, String(vars[key]));
    });
  }, [preset, mode]);

  const palette = paletteFor(preset, mode);

  const antdConfig = useMemo(
    () => ({
      // A dark palette drives antd's dark algorithm even when the app's own
      // light/dark toggle says light — that is how Mono or Midnight theme the
      // whole app, not just the dashboard.
      algorithm: isDarkPalette(palette)
        ? antdTheme.darkAlgorithm
        : antdTheme.defaultAlgorithm,
      token: antdTokensFor(preset, mode),
    }),
    [preset, mode, palette],
  );

  return (
    <AppThemeContext.Provider value={value}>
      <ConfigProvider theme={antdConfig}>{children}</ConfigProvider>
    </AppThemeContext.Provider>
  );
};

/** The active theme plus its save / preview controls. */
export const useAppTheme = (): AppThemeValue => {
  const ctx = useContext(AppThemeContext);
  if (!ctx)
    throw new Error("useAppTheme must be used within an AppThemeProvider");
  return ctx;
};

export default AppThemeProvider;
